import { PasswordInput, Input } from "../../Components/ui/input";
import React, { useEffect, useState } from "react";
import { Button } from "./../../Components/ui/button";
import { useAuth } from "./../../hooks";
import { validatePassword } from "./../../validation";
import { validateConfirmPassword } from "./../../Utils/helperFunction";
import SignUpFlow from "./Layout";
import { userForgotPassword, SendForgotPasswordOtp, loadRecaptcha } from "./../../api";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonLoader from "./../../Components/Loader";

function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const userName = searchParams.get("username") || "";

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({
    password: "",
    confirmPass: "",
    otp: "",
  });

  const [otpData, setOtpData] = useState({ otp: "", otpToken: "" });
  const [otp, setOtp] = useState("");
  const [timeLeft, setTimeLeft] = useState(180);
  const [isResendActive, setIsResendActive] = useState(false);

  const { confirmPass, setConfirmPass, password, setPassword } = useAuth();

  useEffect(() => {
    loadRecaptcha();
  }, []);

  useEffect(() => {
    setPassword("");
    setConfirmPass("");
    if (!userName) {
      navigate("/");
    } else {
      sendOtp();
    }
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setIsResendActive(true);
    }
  }, [timeLeft]);

  const sendOtp = async () => {
    try {
      const data = await SendForgotPasswordOtp({
        username: userName,
        reason: "forgot_password",
      });
      setOtpData((prev) => ({ ...prev, otpToken: data?.data?.otpToken }));
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const handleResendOtp = () => {
    if (isResendActive) {
      setTimeLeft(180);
      sendOtp();
      setErrors((prev) => ({ ...prev, otp: "" }));
      setIsResendActive(false);
      setOtp("");
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setOtp(value);
    setOtpData((prev) => ({ ...prev, otp: value }));
    setErrors((prev) => ({ ...prev, otp: "" }));
  };

  const formatTime = (seconds) => {
    const secs = seconds % 180;
    return `${secs} sec`;
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setErrors((prev) => ({ ...prev, password: "" }));
  };
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPass(value);
    setErrors((prev) => ({ ...prev, confirmPass: "" }));
  };
  const handlePasswordOnBlur = (e) => {
    const passwordValidationErrors = validatePassword(e.target.value);
    setErrors((prev) => ({
      ...prev,
      password:
        passwordValidationErrors.length > 0 ? passwordValidationErrors[0] : "",
      confirmPass: e.target.value === confirmPass ? "" : errors.confirmPass,
    }));
  };
  const handleConfirmPasswordOnBlur = (e) => {
    const error = validatePassword(password);
    const confirmError = validateConfirmPassword(e.target.value, confirmPass);
    error && setErrors((prev) => ({ ...prev, password: error[0] }));
    confirmError &&
      setErrors((prev) => ({ ...prev, confirmPass: confirmError }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate OTP
      if (!otp || otp.length !== 6) {
        setErrors((prev) => ({ ...prev, otp: "Please enter a valid 6-digit OTP" }));
        return;
      }

      // Validate password and confirm password
      const passwordValidationErrors = validatePassword(password);
      const confirmPasswordError = validateConfirmPassword(confirmPass, password);

      // Update errors state
      setErrors((prev) => ({
        ...prev,
        password: passwordValidationErrors.length > 0 ? passwordValidationErrors[0] : "",
        confirmPass: confirmPasswordError || ""
      }));

      // Check if there are any validation errors
      const hasPasswordErrors = passwordValidationErrors.length > 0;
      const hasConfirmPasswordErrors = confirmPasswordError !== "";

      // Only proceed if no errors and fields are not empty
      if (!hasPasswordErrors && !hasConfirmPasswordErrors &&
          confirmPass !== "" && password !== "" && confirmPass === password) {
        setLoading(true);
        await userForgotPassword({
          newPassword: password,
          appName:"MCA",
          username: userName,
          otpToken: otpData.otpToken,
          otp: otpData.otp
        });
        navigate(`/exist?email=${encodeURIComponent(email)}`);
        setPassword("");
        setConfirmPass("");
        setOtp("");
      }
    } catch (error) {
      console.error("Password change failed:", error);
      // Check if the error is related to OTP (incorrect or expired)
      if (/otp/i.test(error?.message || "") || error?.status === 400) {
        setErrors((prev) => ({
          ...prev,
          otp: error?.message || "Incorrect OTP"
        }));
        // If OTP expired, enable resend button and reset timer
        if (error?.message?.toLowerCase().includes("expired")) {
          setTimeLeft(0);
          setIsResendActive(true);
        }
      } else {
        // Show general error message for other errors
        setErrors((prev) => ({
          ...prev,
          password: error?.message || "Failed to update password. Please try again."
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignUpFlow
      headerText={"Update password"}
      ParagraphText={`Create a new password for your account. For security reasons, it can't
          be the same as your last 3 passwords.`}
    >
      <form onSubmit={handleSubmit}>
        <div
          className={` ${
            errors?.confirmPass || errors?.otp ? "gap-y-[12px]" : "space-y-[21px]"
          }
             mb-[27px]`}
        >
          {/* OTP Input Field */}
  
            <div>
              <label
                htmlFor="otp"
                className="block text-[16px] font-geomanistRegular text-greyish mb-[9px] ml-2"
              >
                Enter OTP
              </label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                value={otp}
                onChange={handleOtpChange}
                className={`w-full h-[45px] px-3 py-2 font-geomanistBold text-[16px] ${
                  errors.otp ? "border-red-500" : ""
                }`}
                disabled={loading}
              />
              {errors.otp && (
                <p className="text-red-500 text-[14px] mt-1 ml-2">
                  {errors.otp}
                </p>
              )}
              <div className="flex justify-between items-center text-sm mt-2 ml-2">
                <span className="text-gray-600">
                  {parseInt(formatTime(timeLeft)) > 0
                    ? `${formatTime(timeLeft)} left`
                    : " "}
                </span>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  className={`${
                    isResendActive && !loading
                      ? "text-blue-600 cursor-pointer"
                      : "text-gray-400 cursor-not-allowed"
                  }`}
                  disabled={!isResendActive || loading}
                >
                  Didn't get OTP? Resend
                </button>
              </div>
            </div>
  

          <div>
            <label
              htmlFor="password"
              className="block text-[16px] font-geomanistRegular text-greyish mb-[9px] ml-2"
            >
              Create new Password
            </label>
            <PasswordInput
              id="password"
              value={password}
              error={errors.password}
              // type={eyeView.password ? "text" : "password"}
              onChange={handlePasswordChange}
              onBlur={handlePasswordOnBlur}
              className={`w-full h-[45px] px-3 py-2 font-geomanistBold text-[16px] ${
                errors.password ? "border-red-500" : ""
              }`}
            />
            {errors.password && (
              <p className="text-red-500 text-[14px] mt-1 ml-2">
                {errors.password}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-[16px] font-geomanistRegular text-greyish mb-[9px] ml-2"
            >
              Confirm new Password
            </label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPass}
              error={errors.confirmPass}
              onChange={handleConfirmPasswordChange}
              onBlur={handleConfirmPasswordOnBlur}
              className={`w-full h-[45px] px-3 py-2 font-geomanistBold text-[16px] ${
                errors.confirmPass ? "border-red-500" : ""
              }`}
            />
            {errors.confirmPass && (
              <p className="text-red-500 text-[14px] mt-1 ml-2">
                {errors.confirmPass}
              </p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          variant="primary"
          className="flex"
          disabled={loading}
        >
          {loading && <ButtonLoader className={"mr-2"} />} Update
        </Button>
      </form>
    </SignUpFlow>
  );
}

export default ForgotPassword;
